/**
 * Componente para exibir mensagens de imagem com botões interativos
 * 
 * Usado em campanhas que enviam imagem + botões de ação
 * Renderiza: imagem com preview, texto da mensagem e botões
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { getStorageUrl } from '@/lib/storage';
import { ImageOff, Loader2 } from 'lucide-react';

interface ImageButtonsMessageBubbleProps {
  /** URL ou caminho relativo da imagem */
  imageUrl: string;
  /** Conteúdo/texto da mensagem */
  content?: string;
  /** Botões interativos */
  buttons: Array<{ id: string; label: string }>;
  /** Direção da mensagem (entrada ou saída) */
  direction: 'inbound' | 'outbound';
  /** Callback ao clicar para abrir lightbox */
  onOpenLightbox?: () => void;
}

export function ImageButtonsMessageBubble({
  imageUrl,
  content,
  buttons,
  direction,
  onOpenLightbox,
}: ImageButtonsMessageBubbleProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Converte caminho relativo para URL completa se necessário
  const displayUrl = getStorageUrl(imageUrl) || imageUrl;

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleClick = () => {
    if (!hasError && onOpenLightbox) {
      onOpenLightbox();
    }
  };

  return (
    <div className="space-y-2">
      {/* Container da imagem - margin negativa para bordas finas estilo WhatsApp */}
      <div
        className={cn(
          'relative rounded-lg overflow-hidden cursor-pointer',
          'max-w-[320px] min-w-[100px]',
          '-mx-2.5 md:-mx-4',
          'bg-muted/50'
        )}
        onClick={handleClick}
      >
        {/* Loading skeleton */}
        {isLoading && !hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Error state */}
        {hasError ? (
          <div className="flex flex-col items-center justify-center p-8 bg-muted">
            <ImageOff className="h-8 w-8 text-muted-foreground mb-2" />
            <span className="text-xs text-muted-foreground">
              Imagem não disponível
            </span>
          </div>
        ) : (
          <img
            src={displayUrl}
            alt={content || 'Imagem com botões'}
            className={cn(
              'w-full h-auto object-cover',
              'transition-opacity duration-200',
              isLoading ? 'opacity-0' : 'opacity-100'
            )}
            onLoad={handleLoad}
            onError={handleError}
          />
        )}

        {/* Hover overlay */}
        {!hasError && !isLoading && (
          <div 
            className={cn(
              'absolute inset-0 bg-background/20 opacity-0',
              'hover:opacity-100 transition-opacity',
              'flex items-center justify-center'
            )}
          >
            <span className="text-xs text-foreground/80 bg-background/60 px-2 py-1 rounded">
              Clique para ampliar
            </span>
          </div>
        )}
      </div>

      {/* Container único para texto + botões com largura controlada */}
      {(content || (buttons && buttons.length > 0)) && (
        <div 
          className="rounded-lg overflow-hidden max-w-[280px]"
          style={{ backgroundColor: 'hsl(var(--chat-bubble-inbound))' }}
        >
          {/* Texto da mensagem */}
          {content && (
            <div className="px-4 py-2.5">
              <p 
                className="text-sm whitespace-pre-wrap break-words"
                style={{ color: 'hsl(var(--chat-text-primary))' }}
              >
                {content}
              </p>
            </div>
          )}

          {/* Botões interativos - mesma largura do texto */}
          {buttons && buttons.length > 0 && (
            <div style={{ borderTop: content ? '1px solid hsl(var(--chat-border))' : 'none' }}>
              {buttons.map((button, index) => (
                <div
                  key={button.id || index}
                  className="px-4 py-2.5 text-center text-sm break-words"
                  style={{
                    color: '#00a884',
                    borderTop: index !== 0 ? '1px solid hsl(var(--chat-border))' : 'none'
                  }}
                >
                  {button.label}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
